import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	AlertTriangle,
	BookOpen,
	Clipboard,
	FileText,
	Info,
	ListPlus,
	ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ThinContentParagraphEdits } from "./ThinContentParagraphEdits";
import {
	getWordCountTone,
	readParagraphDiffs,
	type ParagraphDiff,
} from "./thinContentParagraphs";
import type {
	PageIssue,
	ProposedFix,
	ThinContentClassification,
	ThinContentDetails,
	ThinContentExpansionSection,
	ThinContentFix,
} from "@/types/technicalAudit";

interface ThinContentCardProps {
	issue: PageIssue;
	onEdit?: (value: ProposedFix) => void;
}

// Caution default: when the backend cannot confirm the page type we treat the
// page as a CRO-risk conversion page (mirrors backend "unknown -> page").
const DEFAULT_CLASSIFICATION: ThinContentClassification = {
	type: "page",
	expansion_friendly: false,
	cro_risk: true,
	source: "default",
};

// Resolve the blog-vs-page classification. Prefer the top-level field the
// backend emits during serialization; fall back to the packet copy carried on
// the parsed proposed_value/fix; otherwise use the caution default.
function resolveClassification(
	issue: PageIssue,
	fix: ThinContentFix | null,
): ThinContentClassification {
	const top = issue.thin_content_classification;
	if (top && (top.type === "blog" || top.type === "page")) {
		return top;
	}
	const fromFix = fix?.thin_content_classification;
	if (fromFix && (fromFix.type === "blog" || fromFix.type === "page")) {
		return fromFix;
	}
	return DEFAULT_CLASSIFICATION;
}

const actionLabels: Record<string, { label: string; className: string }> = {
	expand: { label: "Expand content", className: "bg-teal-light text-teal-dark border-teal/30" },
	merge: { label: "Merge page", className: "bg-coral-light text-coral-dark border-coral/30" },
	noindex: { label: "Noindex", className: "bg-muted text-muted-foreground border-border" },
	review: { label: "Review", className: "bg-warm-bg text-foreground border-border" },
};

function readThinContentFix(issue: PageIssue): ThinContentFix | null {
	const raw =
		(issue.proposed_fix as unknown) ??
		(issue.approved_value as unknown) ??
		(issue.proposed_value as unknown);
	if (!raw) return null;
	if (typeof raw === "object" && !Array.isArray(raw)) return raw as ThinContentFix;
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (!trimmed) return null;
		try {
			const parsed = JSON.parse(trimmed);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed)
				? (parsed as ThinContentFix)
				: null;
		} catch {
			return null;
		}
	}
	return null;
}

function stringList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function normalizeSections(fix: ThinContentFix | null): ThinContentExpansionSection[] {
	if (!Array.isArray(fix?.recommended_sections)) return [];
	return fix.recommended_sections
		.map((section) => ({
			heading: section.heading || "Suggested content addition",
			purpose: section.purpose || "",
			draft: section.draft || "",
		}))
		.filter((section) => section.heading || section.purpose || section.draft);
}

// Build a markdown editorial brief from the CURRENT edited state so the export
// reflects the specialist's edits. thin_content is recommendation/handoff-only,
// so the brief ends with an explicit manual-application note.
function buildBrief(args: {
	issue: PageIssue;
	fix: ThinContentFix | null;
	classification: ThinContentClassification;
	wordCount: number;
	threshold: number;
	action: string;
	actionLabel: string;
	paragraphDiffs: ParagraphDiff[];
}): string {
	const {
		issue,
		fix,
		classification,
		wordCount,
		threshold,
		action,
		actionLabel,
		paragraphDiffs,
	} = args;
	const lines: string[] = [];
	const title = issue.page_title?.trim();

	// Title block (always printed).
	lines.push("# Content Expansion Brief");
	lines.push("");
	lines.push(`**URL:** ${issue.page_url}`);
	lines.push(`**Page title:** ${title || "(title not captured)"}`);

	// Word count vs threshold (always printed).
	const targetAdditional =
		typeof fix?.target_additional_words === "number"
			? fix.target_additional_words
			: Math.max(threshold - wordCount, 0);
	lines.push("");
	lines.push("## Word count vs threshold");
	lines.push(`- Current: ${wordCount} words`);
	lines.push(`- Review threshold: ${threshold} words`);
	lines.push(`- Target additional words: ${targetAdditional}`);

	// Page classification (always printed).
	const pageTypeLabel = classification.type === "blog"
		? "Blog / post"
		: classification.cro_risk
			? "Conversion page"
			: "Page";
	const sourceLabel =
		classification.source === "default"
			? "default (page type unconfirmed)"
			: classification.source;
	lines.push("");
	lines.push("## Page classification");
	lines.push(`- Type: ${pageTypeLabel}`);
	lines.push(`- Source: ${sourceLabel}`);
	if (classification.cro_risk) {
		lines.push(
			"> CRO note: Conversion page — expanding content may affect CRO. Use this as an editorial brief, not a rewrite instruction.",
		);
	}

	// Action (always printed).
	lines.push("");
	lines.push("## Action");
	lines.push(actionLabel || action || "Review");

	// Strategy / recommendation (omitted when empty).
	const strategy = fix?.summary || fix?.recommendation;
	if (strategy || fix?.reasoning) {
		lines.push("");
		lines.push("## Strategy / recommendation");
		if (strategy) lines.push(strategy);
		if (fix?.reasoning) {
			if (strategy) lines.push("");
			lines.push(fix.reasoning);
		}
	}

	// Paragraph rewrites (omitted when empty). Both sides are printed: a brief
	// that showed only the replacement would be unreviewable away from the app,
	// since the whole point is judging the change against what is live.
	if (paragraphDiffs.length > 0) {
		lines.push("");
		lines.push("## Paragraph rewrites");
		paragraphDiffs.forEach((diff) => {
			lines.push("");
			lines.push(`### Paragraph ${diff.block_index + 1}`);
			lines.push("- **Currently on the page:**");
			lines.push(diff.original);
			lines.push("- **Suggested replacement:**");
			lines.push(diff.proposed);
		});
	}

	// Evidence needed (omitted when empty).
	const evidence = stringList(fix?.evidence_needed);
	if (evidence.length > 0) {
		lines.push("");
		lines.push("## Evidence needed");
		evidence.forEach((item) => lines.push(`- ${item}`));
	}

	// Internal link opportunities (omitted when empty).
	const links = stringList(fix?.internal_link_opportunities);
	if (links.length > 0) {
		lines.push("");
		lines.push("## Internal link opportunities");
		links.forEach((item) => lines.push(`- ${item}`));
	}

	// Trailing manual-application note (always the last line, verbatim).
	lines.push("");
	lines.push("---");
	lines.push(
		"_This is an editorial brief. Apply manually in WordPress; do not auto-publish._",
	);

	return lines.join("\n");
}

export function ThinContentCard({ issue, onEdit }: ThinContentCardProps) {
	const details = issue.issue_details as ThinContentDetails;
	const fix = readThinContentFix(issue);
	const wordCount = Number(details?.word_count ?? 0);
	const threshold = Number(details?.threshold ?? 500);
	const missingWords = Math.max(threshold - wordCount, 0);
	const percentage = Math.min((wordCount / Math.max(threshold, 1)) * 100, 100);
	const tone = getWordCountTone(wordCount, threshold);
	const action = fix?.action || "expand";
	const actionMeta = actionLabels[action] || actionLabels.review;
	const classification = resolveClassification(issue, fix);
	const isBlog = classification.type === "blog";
	// Single page-type badge. For blog/expansion-friendly we collapse to one
	// combined teal badge to reduce noise; pages use a coral CRO tone or a
	// neutral tone when not flagged as conversion-risk.
	const pageTypeBadge = isBlog
		? {
				label: classification.expansion_friendly
					? "Blog / post — expansion-friendly"
					: "Blog / post",
				className: "bg-teal-light text-teal-dark border-teal/30",
			}
		: classification.cro_risk
			? {
					label: "Conversion page",
					className: "bg-coral-light text-coral-dark border-coral/30",
				}
			: {
					label: "Page",
					className: "bg-warm-bg text-foreground border-border",
				};
	// Quiet source-confidence hint (no hint for the trusted index source).
	const sourceHint =
		classification.source === "url_pattern"
			? "Classified from the URL pattern."
			: classification.source === "default"
				? "Page type unconfirmed — treated as a conversion page to be safe."
				: null;
	const hasExpansion =
		Boolean(fix?.summary || fix?.recommendation || fix?.reasoning) ||
		normalizeSections(fix).length > 0 ||
		stringList(fix?.draft_additions).length > 0;
	// Phase 1 (blog-only additive apply): the card-level apply-eligibility signal
	// is blog + not-CRO + an expansion brief exists. The drawer adds the
	// status/approved gate before showing the actual "Apply reviewed expansion"
	// button — here it only drives the blog-vs-page messaging on the card.
	const isBlogApplyEligible = isBlog && !classification.cro_risk && hasExpansion;
	// Generation may flag the brief as not grounded in the live post body
	// (couldn't read it). Phase 1 surfaces a caution + the drawer disables apply.
	const notGrounded = fix?.grounded === false;
	// P1.5b: any apply failure (incl. page-builder bail, which returns a generic
	// error with no machine-readable reason) surfaces a recoverable caution and
	// keeps the Copy brief usable so the editor can apply the sections manually.
	const applyFailed = isBlogApplyEligible && issue.fix_status === "failed";
	const applyError =
		typeof issue.error === "string" && issue.error.trim() ? issue.error.trim() : null;
	const draftKey = `${issue.id}:${JSON.stringify(
		issue.proposed_fix ?? issue.approved_value ?? issue.proposed_value ?? "",
	)}`;
	// The brief's sections/additions are gone; what can be applied is now the
	// accepted paragraph rewrites, which ThinContentParagraphEdits owns and emits.
	// The card keeps only the surrounding context: classification, word count,
	// evidence gaps, grounding and failure states.
	const hasApplicableContent = readParagraphDiffs(
		fix as unknown as Record<string, unknown> | null,
	).length > 0;
	const nothingToApply = isBlogApplyEligible && !hasApplicableContent;

	const [copied, setCopied] = useState(false);
	const handleCopyBrief = async () => {
		const brief = buildBrief({
			issue,
			fix,
			classification,
			wordCount,
			threshold,
			action,
			actionLabel: actionMeta.label,
			paragraphDiffs: readParagraphDiffs(
				fix as unknown as Record<string, unknown> | null,
			),
		});
		await navigator.clipboard?.writeText(brief);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<div className="space-y-4">
			<div className="rounded-lg border border-coral/20 bg-coral-light/50 p-4">
				<div className="flex items-start gap-3">
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-card text-coral-dark">
						<AlertTriangle className="h-5 w-5" />
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<h4 className="font-semibold text-coral-dark">Thin content detected</h4>
							<Badge variant="outline" className={tone.badge}>
								{missingWords > 0 ? `${missingWords} words short` : "At threshold"}
							</Badge>
							<Badge
								variant="outline"
								data-testid="thin-content-classification-badge"
								className={pageTypeBadge.className}
							>
								{isBlog ? (
									<BookOpen className="mr-1 h-3.5 w-3.5" />
								) : null}
								{pageTypeBadge.label}
							</Badge>
							{sourceHint ? (
								<TooltipProvider>
									<Tooltip>
										<TooltipTrigger asChild>
											<span
												data-testid="thin-content-source-hint"
												className="inline-flex items-center text-muted-foreground"
											>
												<Info className="h-3 w-3" />
											</span>
										</TooltipTrigger>
										<TooltipContent>{sourceHint}</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							) : null}
						</div>
						<p className="mt-1 text-sm text-foreground/75">
							This page has {wordCount} words against a {threshold}-word review
							threshold. Treat the generated output as reviewed expansion direction,
							not an automatic page rewrite.
						</p>
					</div>
				</div>
			</div>

			{classification.cro_risk && (
				<div
					data-testid="thin-content-cro-banner"
					className="rounded-lg border border-coral/30 bg-coral-light p-4"
				>
					<div className="flex items-start gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-card text-coral-dark">
							<AlertTriangle className="h-5 w-5" />
						</div>
						<div className="min-w-0 flex-1">
							<h4 className="font-semibold text-coral-dark">
								Conversion page — expand with care
							</h4>
							<p className="mt-1 text-sm text-coral-dark/80">
								Conversion page: expanding content may affect CRO. Use this as an
								editorial brief, not a rewrite instruction.
								{classification.source === "default" ? (
									<span className="text-coral-dark/60">
										{" "}
										(Page type unconfirmed — treated as a conversion page.)
									</span>
								) : null}
							</p>
						</div>
					</div>
				</div>
			)}

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warm-bg text-teal-dark">
							<FileText className="h-4 w-4" />
						</div>
						Word count
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3 pt-0">
					<div className="flex items-end justify-between gap-3">
						<div>
							<div className={cn("text-2xl font-bold tabular-nums", tone.text)}>
								{wordCount}
							</div>
							<div className="text-xs text-muted-foreground">current words</div>
						</div>
						<div className="text-right">
							<div className="text-sm font-medium text-foreground tabular-nums">
								{threshold}
							</div>
							<div className="text-xs text-muted-foreground">review threshold</div>
						</div>
					</div>
					<div className="h-3 overflow-hidden rounded-full bg-warm-bg">
						<div
							className={cn("h-full rounded-full transition-all", tone.bar)}
							style={{ width: `${percentage}%` }}
						/>
					</div>
				</CardContent>
			</Card>

			{!hasExpansion ? (
				<Card className="border-teal/20 bg-teal-light/40 shadow-sm">
					<CardContent className="flex gap-3 py-4 text-sm text-teal-dark">
						<ListPlus className="mt-0.5 h-4 w-4 shrink-0" />
						<div>
							<p className="font-medium">Expansion brief not generated yet</p>
							<p className="mt-1 text-teal-dark/80">
								Generate an expansion brief to draft new sections, proof gaps, and
								internal-link ideas for editorial review.
							</p>
						</div>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-4">
					<Card className="border-teal/20 bg-teal-light/40 shadow-sm">
						<CardHeader className="pb-3">
							<CardTitle className="flex flex-wrap items-center gap-2 text-sm font-semibold text-teal-dark">
								<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-card text-teal-dark">
									<BookOpen className="h-4 w-4" />
								</div>
								Content expansion brief
								<Badge variant="outline" className={actionMeta.className}>
									{actionMeta.label}
								</Badge>
								{fix?.target_additional_words ? (
									<Badge variant="outline" className="bg-card text-foreground border-teal/20">
										+{fix.target_additional_words} target words
									</Badge>
								) : null}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							{(fix?.summary || fix?.recommendation) && (
								<div className="rounded-lg border border-teal/20 bg-card p-3 text-sm text-foreground">
									<p className="text-xs font-medium uppercase tracking-wide text-teal-dark">
										Strategy
									</p>
									<p className="mt-1">{fix.summary || fix.recommendation}</p>
								</div>
							)}
							{fix?.reasoning && (
								<div className="rounded-lg border border-teal/20 bg-card p-3 text-sm text-foreground">
									<p className="text-xs font-medium uppercase tracking-wide text-teal-dark">
										Reasoning
									</p>
									<p className="mt-1">{fix.reasoning}</p>
								</div>
							)}
						</CardContent>
					</Card>

					<ThinContentParagraphEdits
						issue={issue}
						fix={fix as unknown as Record<string, unknown> | null}
						onEdit={onEdit}
					/>

					{stringList(fix?.evidence_needed).length > 0 && (
						<Card className="border-coral/20 bg-coral-light/30 shadow-sm">
							<CardContent className="space-y-2 p-4">
								<div className="flex items-center gap-2 text-sm font-semibold text-coral-dark">
									<ShieldCheck className="h-4 w-4" />
									Evidence needed before publishing
								</div>
								<ul className="space-y-1 text-sm text-foreground/80">
									{stringList(fix?.evidence_needed).map((item, index) => (
										<li key={`${draftKey}:evidence:${index}`}>- {item}</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

				</div>
			)}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<Button
					type="button"
					variant="outline"
					onClick={handleCopyBrief}
					data-testid="thin-content-copy-brief"
					className="gap-2"
				>
					<Clipboard className="h-4 w-4" />
					{copied ? "Copied" : "Copy brief"}
				</Button>
			</div>

			{applyFailed && (
				<div
					data-testid="thin-content-apply-failure"
					className="flex items-start gap-3 rounded-lg border border-coral/30 bg-coral-light p-4"
				>
					<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-coral-dark" />
					<div className="min-w-0">
						<p className="text-sm text-coral-dark">
							Couldn't apply this expansion automatically. Export the brief and
							add the sections manually in WordPress.
						</p>
						{applyError && (
							<p className="mt-1 break-words text-xs text-muted-foreground">
								{applyError}
							</p>
						)}
					</div>
				</div>
			)}

				{isBlogApplyEligible && notGrounded && (
					<div
						data-testid="thin-content-fetch-failure"
						className="flex items-start gap-3 rounded-lg border border-coral/30 bg-coral-light p-4"
					>
						<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-coral-dark" />
						<p className="text-sm text-coral-dark">
							Couldn't read the current post; this brief isn't grounded in live
							content.
						</p>
					</div>
				)}

				{nothingToApply && (
					<div
						data-testid="thin-content-nothing-to-apply"
						className="flex items-start gap-3 rounded-lg border border-coral/30 bg-coral-light p-4"
					>
						<AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-coral-dark" />
						<p className="text-sm text-coral-dark">
							Nothing to apply yet — add draft copy to at least one section.
						</p>
					</div>
				)}

				{isBlogApplyEligible ? (
					<Card className="border-border bg-warm-bg shadow-sm">
						<CardContent
							data-testid="thin-content-additive-note"
							className="py-3 text-xs text-muted-foreground"
						>
							Reviewed sections can be applied to this post. Apply{" "}
							<span className="font-medium text-foreground">appends</span> them to
							the existing content in WordPress — nothing already on the page is
							changed or removed. Review and edit the drafts below before applying.
						</CardContent>
					</Card>
				) : (
					<Card className="border-border bg-warm-bg shadow-sm">
						<CardContent
							data-testid="thin-content-recommendation-note"
							className="py-3 text-xs text-muted-foreground"
						>
							Marking this ready approves it for editorial handoff and review — it
							is not applied to WordPress. Expansion stays manual: an editor places
							the reviewed copy in WordPress or the page builder.
						</CardContent>
					</Card>
				)}
			</div>
		);
}
